// Shared post-add offer. Shopify owns cart prices and applies the configured discount.
const shownProducts = new Set();
const productRequests = new Map();
const collectionRequests = new Map();
const closingDialogs = new WeakMap();
const storageKey = 'seika.second-item-offer.shown.v1';
const maxSessionOffers = 2;
let activePresentation = null;
let cartNotice = '';
let cartNoticeIsError = false;

export function offerPrice(price, percent = 20) {
  return Math.round(Number(price) * (100 - percent) / 100);
}

export function isBeanie(product) {
  if (product?.category === 'beanie' || [15290234798428, 15245413253468, 15358900732252, 15879013040476].includes(Number(product?.product_id || product?.id))) return true;
  return /\bbeanies?\b/i.test([product?.type, product?.product_type, product?.template_suffix, product?.title, product?.product_title, product?.handle].filter(Boolean).join(' ').replace(/[_-]/g, ' '));
}

function isLongsleeve(product) {
  if (product?.category === 'longsleeve' || [15483785150812, 15464716501340].includes(Number(product?.product_id || product?.id))) return true;
  return /\blong\s*sleeves?\b/i.test([product?.type, product?.product_type, product?.template_suffix, product?.title, product?.product_title, product?.handle].filter(Boolean).join(' ').replace(/[_-]/g, ' '));
}

function isCap(product) {
  if (product?.category === 'caps') return true;
  return /\bcaps?\b/i.test([product?.type, product?.product_type, product?.template_suffix, product?.title, product?.product_title, product?.handle].filter(Boolean).join(' ').replace(/[_-]/g, ' '));
}

function offerCategory(product) {
  return isBeanie(product) ? 'beanie' : isLongsleeve(product) ? 'longsleeve' : isCap(product) ? 'caps' : null;
}

export function offerForCart(cart, sourceProduct, products = []) {
  const category = offerCategory(sourceProduct);
  if (!category) return null;
  const beanie = category === 'beanie';
  const familyIds = new Set(products.filter(product => offerCategory(product) === category).map(product => Number(product.id)));
  familyIds.add(Number(sourceProduct.id));
  const eligible = line => !line.selling_plan_allocation && (familyIds.has(Number(line.product_id)) || offerCategory(line) === category);
  const quantity = (cart?.items || []).filter(eligible).reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  if (beanie) {
    // The existing Longsleeve + two-beanie promotion can win over these tiers.
    // Let mixed carts use the cart's recommendations and Shopify's actual prices.
    if ((cart?.items || []).some(line => Number(line.quantity) > 0 && isLongsleeve(line))) return null;
    if (quantity !== 1 && quantity !== 2) return null;
    return { category: 'beanie', percent: quantity === 1 ? 25 : 50, quantity, key: `beanie:${quantity + 1}`, eligible };
  }
  // A longsleeve can also qualify the competing two-beanie promotion. Do not
  // promise both Buy-X-get-Y rules on the same qualifying item.
  if (category === 'longsleeve' && (cart?.items || []).some(line => Number(line.quantity) > 0 && isBeanie(line))) return null;
  return quantity === 1 ? { category, percent: 20, quantity, key: `${category}:2`, eligible } : null;
}

export function offerCopy(offer) {
  return {
    heading: `Get a ${offer.quantity === 2 ? 'third' : 'second'} one for ${offer.percent}% off`,
    submit: 'Add to cart'
  };
}

// Buy-X-get-Y discounts may be allocated to an existing cheaper item. The 50%
// beanie tier replaces the 25% tier, so validate its total saving, not their sum.
export function hasOfferSavings(before, after, added, offer) {
  const savings = cart => (cart?.items || []).filter(offer.eligible).reduce((sum, line) => {
    const original = Number(line.original_line_price);
    const final = Number(line.final_line_price);
    return sum + (Number.isSafeInteger(original) && Number.isSafeInteger(final) ? Math.max(0, original - final) : 0);
  }, 0);
  const original = Number(added?.original_line_price);
  if (added?.quantity !== 1 || !Number.isSafeInteger(original) || original <= 0) return false;
  const units = (after?.items || []).filter(offer.eligible).filter(line => Number(line.quantity) > 0)
    .map(line => Number(line.original_line_price) / Number(line.quantity));
  if (!units.length || units.some(price => !Number.isFinite(price) || price <= 0)) return false;
  const cheapest = Math.min(...units);
  return savings(after) >= Math.floor(cheapest * offer.percent / 100);
}

export function hasOfferDiscount(line, percent = 20) {
  const validCents = value => (typeof value === 'number' || typeof value === 'string' && /^\d+$/.test(value))
    && Number.isSafeInteger(Number(value)) && Number(value) >= 0;
  if (!validCents(line?.original_line_price) || !validCents(line?.final_line_price)) return false;
  const original = Number(line?.original_line_price);
  const final = Number(line?.final_line_price);
  return line?.quantity === 1 && original > 0 && final <= offerPrice(original, percent);
}

export function normalizeProduct(product) {
  const variants = (product.variants || []).map(variant => ({
    ...variant,
    id: Number(variant.id),
    options: variant.options || [variant.option1, variant.option2, variant.option3].filter(value => value != null),
    image: variant.featured_image?.src || variant.featured_image || variant.image?.src || variant.image || ''
  })).filter(variant => Number.isSafeInteger(variant.id) && variant.id > 0);
  const options = (product.options || []).map((option, index) => ({
    name: typeof option === 'string' ? option : option.name,
    values: [...new Set(variants.map(variant => variant.options[index]).filter(value => value != null))]
  }));
  return { ...product, id: Number(product.id), variants, options };
}

function rootUrl() { return window.Shopify?.routes?.root || '/'; }
function setError(modal, message = '') {
  const error = modal.querySelector('[data-second-item-error]');
  error.textContent = message;
  error.hidden = !message;
}
function money(price) {
  return new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency: window.Shopify?.currency?.active || 'EUR'
  }).format(Number(price) / 100);
}
function imageUrl(value) {
  if (!value) return '';
  try {
    const url = new URL(value, location.origin);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch { return ''; }
}
async function request(path, body, responseType = 'json') {
  let response;
  let data;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10000);
  try {
    response = await fetch(`${rootUrl()}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { Accept: responseType === 'text' ? 'text/html' : 'application/json', ...(body ? { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: controller.signal
    });
    data = responseType === 'text' ? await response.text() : await response.json();
  } catch (error) {
    // A lost response does not establish whether Shopify committed a cart write.
    if (body) error.uncertainWrite = true;
    throw error;
  } finally {
    clearTimeout(timer);
  }
  if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'Please try again.');
  return data;
}
function remember(productId) {
  shownProducts.add(String(productId));
  try { sessionStorage.setItem(storageKey, JSON.stringify([...shownProducts])); } catch { /* Memory fallback when storage is unavailable. */ }
}

function readOfferProducts(container, sourceProductId) {
  const script = container.querySelector(`[data-second-item-products-data][data-source-product-id="${sourceProductId}"]`);
  if (!script) return null;
  try {
    const data = JSON.parse(script.textContent);
    if (Number(data.sourceProductId) !== sourceProductId || !Array.isArray(data.products)) return null;
    const ids = new Set();
    const products = data.products.map(normalizeProduct).filter(product => {
      if (!Number.isSafeInteger(product.id) || product.id <= 0 || ids.has(product.id) || product.type === 'Gift Card') return false;
      ids.add(product.id);
      return product.variants.some(variant => Number.isSafeInteger(Number(variant.price)) && Number(variant.price) > 0);
    });
    return products.some(product => product.id === sourceProductId) ? products : null;
  } catch { return null; }
}

async function loadOfferProducts(item, handle) {
  const sourceProductId = Number(item.product_id);
  // The PDP embeds the very same collection window used by its product switcher.
  const embedded = readOfferProducts(document, sourceProductId);
  if (embedded) return embedded;
  if (!collectionRequests.has(handle)) {
    collectionRequests.set(handle, request(`products/${encodeURIComponent(handle)}?section_id=cart-offer-products`, undefined, 'text')
      .then(html => {
        const products = readOfferProducts(new DOMParser().parseFromString(html, 'text/html'), sourceProductId);
        if (!products) throw new Error('Product selection unavailable.');
        return products;
      }).catch(error => { collectionRequests.delete(handle); throw error; }));
  }
  try { return await collectionRequests.get(handle); }
  catch {
    // Collection rendering can fail independently of a successful first add.
    if (!productRequests.has(handle)) productRequests.set(handle, request(`products/${encodeURIComponent(handle)}.js`)
      .catch(error => { productRequests.delete(handle); throw error; }));
    return [normalizeProduct(await productRequests.get(handle))];
  }
}

function buildOptions(modal, product, initialId, offer) {
  const initial = product.variants.find(variant => variant.id === Number(initialId) && variant.available)
    || product.variants.find(variant => variant.available);
  const state = { variant: initial, selected: [...initial.options], busy: false };
  const optionsContainer = modal.querySelector('[data-second-item-options]');
  const colorGroup = modal.querySelector('[data-second-item-color-group]');
  const sizeGroup = modal.querySelector('[data-second-item-size-group]');
  optionsContainer.querySelectorAll('[data-second-item-extra-group]').forEach(group => group.remove());
  [colorGroup, sizeGroup].forEach(group => {
    group.hidden = true;
    group.querySelector('[data-second-item-colors], [data-second-item-sizes]').replaceChildren();
  });
  const fields = [];
  const isSizeOption = index => /^(size|größe|grösse)$/i.test(product.options[index]?.name?.trim());
  const isColorOption = index => /^(color|colour|farbe)$/i.test(product.options[index]?.name?.trim());
  const colorIndex = product.options.findIndex((option, index) => isColorOption(index));
  // Some products call their visual variant "Style" or "Design" instead of "Color".
  const photoIndex = colorIndex >= 0 ? colorIndex : product.options.findIndex((option, index) => !isSizeOption(index)
    && new Set(option.values.map(value => product.variants.find(variant => variant.options[index] === value && variant.image)?.image).filter(Boolean)).size > 1);
  const isVisualOption = index => isColorOption(index) || index === photoIndex;
  // Earlier selections constrain later options. Later options may reset to make
  // all available combinations reachable, while a size change keeps its color.
  const matchesOption = (variant, index, value) => variant.available && variant.options[index] === value
    && (isVisualOption(index) || variant.options.every((entry, position) => position === index
      || (!isVisualOption(position) && position > index) || entry === state.selected[position]));
  product.options.forEach((option, index) => {
    if (option.name === 'Title' && option.values.length === 1 && option.values[0] === 'Default Title') return;
    const isColor = isVisualOption(index);
    const isSize = isSizeOption(index);
    const group = isColor ? colorGroup : isSize ? sizeGroup : sizeGroup.cloneNode(true);
    if (!isColor && !isSize) {
      group.removeAttribute('data-second-item-size-group');
      group.dataset.secondItemExtraGroup = '';
      optionsContainer.append(group);
    }
    group.hidden = false;
    const selectedLabel = group.querySelector('[data-second-item-selected-color], [data-second-item-selected-size]');
    group.querySelector('legend').replaceChildren(document.createTextNode(`${option.name}: `), selectedLabel);
    const values = group.querySelector('[data-second-item-colors], [data-second-item-sizes]');
    values.replaceChildren();
    const inputs = [];
    option.values.forEach((value, valueIndex) => {
      const template = modal.querySelector(isColor ? '[data-second-item-color-template]' : '[data-second-item-size-template]');
      const element = template.content.firstElementChild.cloneNode(true);
      const input = element.querySelector('input');
      const label = element.querySelector('label');
      input.id = `second-item-${product.id}-${index}-${valueIndex}`;
      input.name = `second-item-option-${index}`;
      input.value = value;
      input.required = true;
      label.htmlFor = input.id;
      label.querySelector('[data-second-item-option-label]').textContent = value;
      if (isColor) {
        const candidates = product.variants.filter(variant => variant.options[index] === value && variant.image);
        const photo = candidates.find(variant => variant.available && variant.options.every((entry, position) => position === index || entry === state.selected[position]))?.image
          || candidates.find(variant => variant.available)?.image || candidates[0]?.image
          || product.featured_image || product.images?.[0];
        const src = imageUrl(photo);
        if (src) {
          label.className = 'thumbnail-swatch';
          const img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.width = 64;
          img.height = 80;
          img.loading = 'lazy';
          label.append(img);
        } else {
          label.className = 'block-swatch';
          label.querySelector('span').classList.remove('sr-only');
        }
        label.title = value;
      }
      input.addEventListener('change', () => {
        if (state.busy) return;
        state.selected[index] = value;
        // Color changes may need a different available size. Size changes never silently change color.
        if (isColor) {
          const sameSelection = product.variants.find(variant => variant.available && variant.options.every((entry, position) => entry === state.selected[position]));
          const next = sameSelection || product.variants.find(variant => variant.available && variant.options[index] === value);
          if (next) state.selected = [...next.options];
        } else {
          const next = product.variants.find(variant => variant.available && variant.options.every((entry, position) => entry === state.selected[position]))
            || product.variants.find(variant => matchesOption(variant, index, value));
          if (next) state.selected = [...next.options];
        }
        if (!state.uncertain) setError(modal);
        update();
      });
      inputs.push(input);
      values.append(element);
    });
    fields.push({ index, isColor, inputs, selectedLabel });
  });

  function update() {
    state.variant = product.variants.find(variant => variant.available && variant.options.every((value, index) => value === state.selected[index])) || null;
    fields.forEach(field => {
      field.selectedLabel.textContent = state.selected[field.index] || '';
      field.inputs.forEach(input => {
        const available = product.variants.some(variant => matchesOption(variant, field.index, input.value));
        input.checked = input.value === state.selected[field.index];
        input.disabled = state.busy || state.uncertain || !available;
        input.setAttribute('aria-label', available ? input.value : `${input.value} — sold out`);
      });
    });
    const shown = state.variant || product.variants.find(variant => variant.options.every((value, index) => value === state.selected[index])) || initial;
    modal.querySelector('[data-second-item-original-price]').textContent = money(shown.price);
    modal.querySelector('[data-second-item-price]').textContent = money(offerPrice(shown.price, offer.percent));
    modal.querySelector('[data-second-item-variant]').value = state.variant?.id || '';
    modal.querySelector('[data-second-item-submit]').disabled = state.busy || state.uncertain || !state.variant;
    const availability = modal.querySelector('[data-second-item-availability]');
    availability.hidden = !!state.variant;
    availability.textContent = state.variant ? '' : 'This combination is sold out. Choose another size or color.';
  }
  state.update = update;
  update();
  return state;
}

function buildProductChoices(modal, products, sourceProduct, initialId, offer) {
  const group = modal.querySelector('[data-second-item-products-group]');
  const values = modal.querySelector('[data-second-item-products]');
  const template = modal.querySelector('[data-second-item-product-template]');
  const inputs = [];
  const rememberedVariants = new Map([[sourceProduct.id, initialId]]);
  const selection = { product: sourceProduct, state: null };
  const canChoose = product => product.variants.some(variant => variant.available && Number(variant.price) > 0);
  if (values) values.replaceChildren();
  if (group) group.hidden = products.length < 2;

  function selectProduct(product) {
    if (selection.state?.busy || selection.state?.uncertain || !canChoose(product)) return;
    if (selection.state?.variant) rememberedVariants.set(selection.product.id, selection.state.variant.id);
    selection.product = product;
    const state = buildOptions(modal, product, rememberedVariants.get(product.id), offer);
    selection.state = state;
    modal.querySelector('[data-second-item-title]').textContent = product.title;
    setError(modal);
    const updateOptions = state.update;
    state.update = () => {
      updateOptions();
      inputs.forEach(({ input, product: option }) => {
        input.checked = option.id === product.id;
        input.disabled = state.busy || state.uncertain || !canChoose(option);
      });
    };
    state.update();
  }

  if (values && template && products.length > 1) products.forEach(product => {
    const element = template.content.firstElementChild.cloneNode(true);
    const input = element.querySelector('input');
    const label = element.querySelector('label');
    const available = canChoose(product);
    input.id = `second-item-product-${sourceProduct.id}-${product.id}`;
    input.name = 'second-item-product';
    input.value = String(product.id);
    input.required = true;
    input.setAttribute('aria-label', available ? product.title : `${product.title} — sold out`);
    label.htmlFor = input.id;
    label.title = product.title;
    label.querySelector('[data-second-item-product-label]').textContent = product.title
      .replace(/\s+(?:satin[- ]lined|embroidered|waffle).*$/i, '').replace(/^["“”„]+|["“”„]+$/g, '');
    label.querySelector('[data-second-item-product-badge]').textContent = `${offer.percent}% OFF`;
    const firstVariant = product.variants.find(variant => variant.available) || product.variants[0];
    const image = label.querySelector('[data-second-item-product-image]');
    const src = imageUrl(product.featured_image || firstVariant?.image || product.images?.[0]);
    image.hidden = !src;
    image.alt = '';
    if (src) image.src = src;
    const prices = product.variants.filter(variant => !available || variant.available).map(variant => Number(variant.price));
    const price = Math.min(...prices);
    const prefix = Math.max(...prices) > price ? 'From ' : '';
    const original = label.querySelector('[data-second-item-product-original-price]');
    original.textContent = `${prefix}${money(price)}`;
    original.hidden = !available;
    label.querySelector('[data-second-item-product-price]').textContent = available ? `${prefix}${money(offerPrice(price, offer.percent))}` : 'Sold out';
    input.addEventListener('change', () => { selectProduct(product); });
    inputs.push({ input, product });
    values.append(element);
  });
  selectProduct(sourceProduct);
  return selection;
}

async function closeOtherDialogs(modal) {
  for (const dialog of document.querySelectorAll('x-modal, quick-buy-modal, cart-drawer')) {
    if (dialog === modal) continue;
    if (dialog.open) await dialog.hide();
    else await closingDialogs.get(dialog)?.promise;
  }
}

async function showCart() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
    location.href = `${rootUrl()}cart`;
    return;
  }
  const refreshed = new Promise(resolve => {
    const timer = setTimeout(() => done(false), 5000);
    function done(event) { clearTimeout(timer); drawer.removeEventListener('cart-drawer:refreshed', done); resolve(!!event); }
    drawer.addEventListener('cart-drawer:refreshed', done, { once: true });
  });
  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  if (await refreshed) {
    if (cartNotice) {
      const notice = document.createElement('p');
      notice.className = `SecondItemOffer__CartNotice${cartNoticeIsError ? ' is-error' : ''}`;
      notice.setAttribute('role', cartNoticeIsError ? 'alert' : 'status');
      notice.textContent = cartNotice;
      drawer.prepend(notice);
      cartNotice = '';
    }
    await drawer.show();
  }
  else location.href = `${rootUrl()}cart`;
}

async function acceptOffer(modal, sourceProduct, products, state, offer) {
  const submit = modal.querySelector('[data-second-item-submit]');
  const continueButton = modal.querySelector('[data-second-item-continue]');
  const closeButton = modal.querySelector('[data-second-item-close]');
  const label = submit.textContent;
  const instance = crypto.randomUUID();
  state.busy = true;
  state.update();
  continueButton.disabled = true;
  closeButton.disabled = true;
  submit.setAttribute('aria-busy', 'true');
  submit.textContent = 'Adding…';
  setError(modal);
  try {
    const before = await request('cart.js');
    if (!before.items.some(line => Number(line.product_id) === sourceProduct.id && line.quantity > 0)) {
      throw new Error('Your first item is no longer in the cart. Add it again to use this offer.');
    }
    const currentOffer = offerForCart(before, sourceProduct, products);
    if (!currentOffer || currentOffer.key !== offer.key) {
      throw new Error('Your cart has changed. Decline this offer and review your cart before adding another item.');
    }
    await request('cart/add.js', { items: [{
      id: state.variant.id, quantity: 1,
      properties: { _seika_second_item_offer: String(offer.percent), _seika_offer_product: String(sourceProduct.id), _seika_offer_instance: instance }
    }] });
    const cart = await request('cart.js');
    const added = cart.items.find(line => line.properties?._seika_offer_instance === instance);
    if (!added || added.quantity !== 1) throw new Error('We could not confirm your second item. Please try again.');
    const discounted = hasOfferSavings(before, cart, added, offer);
    submit.textContent = discounted ? 'Added — cart saving confirmed' : 'Added';
    cartNotice = discounted ? '' : 'Your item was added, but Shopify has not confirmed the expected cart saving. Review your cart price before checkout.';
    cartNoticeIsError = !discounted;
    await modal.hide();
    return { cart, discounted };
  } catch (error) {
    // Recover a possibly completed request by its unique line property, never by variant ID.
    // Roll back failed or uncertain adds only; a missing discount is not an add failure.
    try {
      const cart = await request('cart.js');
      const added = cart.items.find(line => line.properties?._seika_offer_instance === instance);
      if (added) await request('cart/change.js', { id: added.key, quantity: 0 });
      if (error.uncertainWrite && !added) throw error;
      setError(modal, error.message || 'The second item could not be added. Please try again.');
    } catch {
      state.uncertain = true;
      cartNotice = 'We could not confirm this offer. Please review the extra item in your cart before checkout.';
      cartNoticeIsError = true;
      setError(modal, cartNotice);
      continueButton.textContent = 'Review cart';
    }
    return false;
  } finally {
    state.busy = false;
    state.update();
    submit.disabled ||= !!state.uncertain;
    submit.removeAttribute('aria-busy');
    submit.textContent = label;
    continueButton.disabled = false;
    closeButton.disabled = false;
    if (!modal.open && modal.querySelector('[data-second-item-error]').textContent) {
      const announcement = document.getElementById('error-announcement') || document.querySelector('[role="alert"]:not([data-second-item-error])');
      cartNotice = modal.querySelector('[data-second-item-error]').textContent;
      cartNoticeIsError = true;
      if (announcement) announcement.textContent = cartNotice;
    }
  }
}

function requireExplicitDecline(modal, signal) {
  // Keep the theme's focus trap and scroll lock, but close this offer only from
  // its explicit buttons or after a successful add. Other dialogs are unchanged.
  Object.defineProperty(modal, 'clickOutsideDeactivates', { configurable: true, value: false });
  modal.hideForOutsideClickTarget = () => false;
  const stopDismissal = event => { event.preventDefault(); event.stopImmediatePropagation(); };
  // Capture on the shadow root, before the theme's overlay hide handler. This
  // survives the modal being moved to <body> and its listeners being rebound.
  modal.shadowRoot?.addEventListener('click', event => {
    if (event.target === modal.getShadowPartByName('overlay')) stopDismissal(event);
  }, { capture: true, signal });
  modal.addEventListener('dialog:force-close', stopDismissal, { capture: true, signal });
  window.addEventListener('keydown', event => {
    if (modal.open && event.key === 'Escape') stopDismissal(event);
  }, { capture: true, signal });
}

async function present(context = {}) {
  if (context.source !== 'product-page') return false;
  if (activePresentation) {
    if (!(await activePresentation)) await showCart();
    return true;
  }
  activePresentation = presentOffer(context);
  try { return await activePresentation; }
  finally { activePresentation = null; }
}

async function presentOffer({ items = [], cart } = {}) {
  const modal = document.querySelector('[data-second-item-offer]');
  if (!modal || modal.dataset.enabled === 'false') return false;
  const item = items.find(item => {
    const id = String(item.product_id || '');
    const quantity = (cart?.items || []).filter(line => String(line.product_id) === id).reduce((sum, line) => sum + line.quantity, 0);
    return id && quantity > 0 && !item.selling_plan_allocation && !item.properties?._seika_second_item_offer;
  });
  if (!item) return false;
  const handle = item.handle || cart.items.find(line => line.product_id === item.product_id)?.handle;
  if (!handle) return false;
  let shown = false;
  let transaction = null;
  let controller;
  try {
    let products = await loadOfferProducts(item, handle);
    const product = products.find(product => product.id === Number(item.product_id));
    if (!product || !product.variants.some(variant => variant.available && variant.price > 0) || product.type === 'Gift Card') return false;
    products = products.filter(candidate => offerCategory(candidate) === offerCategory(product));
    const offer = offerForCart(cart, product, products);
    if (!offer || shownProducts.has(offer.key)) return false;
    // Beanie tiers are independent of the existing cap/product session limit.
    if (offer.category !== 'beanie' && [...shownProducts].filter(key => !key.startsWith('beanie:')).length >= maxSessionOffers) return false;
    await customElements.whenDefined('x-modal');
    await closeOtherDialogs(modal);
    if (cart.sections) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true, detail: { baseEvent: 'variant:add', deferCartOpen: true, cart }
      }));
    }
    const beanie = offer.category === 'beanie';
    const copy = offerCopy(offer);
    modal.querySelector('[data-second-item-heading]').textContent = copy.heading;
    modal.querySelector('[data-second-item-submit]').textContent = copy.submit;
    modal.querySelector('[data-second-item-confirmed]').textContent = beanie && offer.quantity === 2 ? '2 BEANIES IN YOUR CART' : 'ADDED TO YOUR CART';
    modal.querySelector('[data-second-item-continue]').textContent = 'No thanks';
    modal.querySelector('[data-second-item-close]').disabled = false;
    setError(modal);
    const selection = buildProductChoices(modal, products, product, item.variant_id || item.id, offer);
    controller = new AbortController();
    requireExplicitDecline(modal, controller.signal);
    const hidden = new Promise(resolve => modal.addEventListener('dialog:after-hide', resolve, { once: true, signal: controller.signal }));
    modal.querySelector('[data-second-item-form]').addEventListener('submit', event => {
      event.preventDefault();
      const state = selection.state;
      if (state.busy || state.uncertain || !state.variant) return;
      transaction = acceptOffer(modal, product, products, state, offer);
    }, { signal: controller.signal });
    modal.querySelector('[data-second-item-continue]').addEventListener('click', () => { if (!selection.state.busy) void modal.hide(); }, { signal: controller.signal });
    modal.querySelector('[data-second-item-close]').addEventListener('click', () => { if (!selection.state.busy) void modal.hide(); }, { signal: controller.signal });
    modal.style.removeProperty('opacity');
    modal.getShadowPartByName('content')?.style.removeProperty('transform');
    modal.getShadowPartByName('overlay')?.style.removeProperty('opacity');
    shown = true;
    await modal.show();
    remember(offer.key);
    await hidden;
    const accepted = transaction ? await transaction : null;
    controller.abort();
    // One second-beanie acceptance can unlock the third-beanie tier. Only chain
    // it when Shopify confirmed the previous saving; otherwise show the warning.
    if (beanie && offer.quantity === 1 && accepted?.discounted) {
      if (await presentOffer({ items: [item], cart: accepted.cart })) return true;
    }
    await showCart();
    return true;
  } catch (error) {
    console.warn('Second-piece offer unavailable:', error.message);
    if (shown) { await showCart(); return true; }
    return false;
  } finally {
    controller?.abort();
  }
}

if (typeof window !== 'undefined') {
  try { JSON.parse(sessionStorage.getItem(storageKey) || '[]').forEach(id => shownProducts.add(String(id))); } catch { /* Storage may be unavailable. */ }
  document.addEventListener('dialog:before-hide', event => {
    let finish;
    const promise = new Promise(resolve => { finish = resolve; });
    closingDialogs.set(event.target, { promise, finish });
  }, true);
  document.addEventListener('dialog:after-hide', event => {
    closingDialogs.get(event.target)?.finish();
    closingDialogs.delete(event.target);
  }, true);
  window.SeikaSecondItemOffer = { present };
}
